import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Mail, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LS_KEY = "afrimarket_newsletter_seen";
const SUCCESS_KEY = "afrimarket_newsletter_done";

/**
 * Exit-intent newsletter capture popup.
 * - Desktop: triggers when mouse leaves viewport towards the top (close-tab gesture)
 * - Mobile: triggers after 25s on page + 50% scroll depth (proxy for engagement-then-leave)
 * - Once per session; never again after a successful subscription
 * - Offers 10% off welcome code mailed via backend /api/newsletter/subscribe
 */
const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Suppress if user has already subscribed (permanent) or already saw it (session)
    if (window.localStorage.getItem(SUCCESS_KEY)) return;
    if (window.sessionStorage.getItem(LS_KEY)) return;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // Desktop: exit-intent on mouse leaving via top
    const onMouseLeave = (e) => {
      if (e.clientY < 5) {
        window.sessionStorage.setItem(LS_KEY, "1");
        setOpen(true);
        document.removeEventListener("mouseleave", onMouseLeave);
      }
    };

    // Mobile fallback: time + scroll trigger
    let mobileTimer = null;
    if (isMobile) {
      mobileTimer = window.setTimeout(() => {
        const scrolled = window.scrollY + window.innerHeight;
        const total = document.documentElement.scrollHeight;
        if (total > 0 && scrolled / total > 0.5) {
          window.sessionStorage.setItem(LS_KEY, "1");
          setOpen(true);
        }
      }, 25_000);
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, source: "exit_intent" });
      setCode(data.code);
      window.localStorage.setItem(SUCCESS_KEY, "1");
      if (typeof window.gtag === "function") {
        window.gtag("event", "newsletter_signup", { method: "exit_intent" });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => setOpen(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={close}
      data-testid="newsletter-popup"
    >
      <div
        className="relative bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-[fadeIn_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#1A2024] hover:bg-black/5 rounded-full transition-colors"
          data-testid="newsletter-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top accent bar — African-print inspired */}
        <div className="h-1.5 bg-gradient-to-r from-[#C05A3C] via-[#D9923B] to-[#4A5D23]" />

        <div className="p-7">
          {!code ? (
            <>
              <div className="w-12 h-12 rounded-full bg-[#C05A3C]/15 text-[#C05A3C] flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6" />
              </div>
              <h2 className="font-display text-2xl text-[#1A2024] leading-tight mb-1">
                Get 10% off your first order
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Hand-picked African essentials, monthly drops, and your first-order discount — straight to your inbox. No spam, ever.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="newsletter-email"
                    className="w-full pl-10 pr-3 py-3 border border-black/15 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="w-full bg-[#C05A3C] hover:bg-[#D46B4E] text-white font-semibold py-3 rounded-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Send me my 10% code
                </button>
              </form>
              <p className="text-[11px] text-muted-foreground mt-3 text-center">
                Unsubscribe anytime · We never share your email
              </p>
            </>
          ) : (
            <div className="text-center py-2" data-testid="newsletter-success">
              <div className="w-14 h-14 rounded-full bg-[#4A5D23]/15 text-[#4A5D23] flex items-center justify-center mb-4 mx-auto">
                <Sparkles className="w-7 h-7" />
              </div>
              <h2 className="font-display text-2xl text-[#1A2024] leading-tight mb-1">
                Karibu! 🌍 Check your inbox
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Your 10% discount code is on its way to <strong>{email}</strong>. Or use it right now:
              </p>
              <div className="bg-[#1A2024] text-white font-mono text-lg tracking-widest py-4 px-4 rounded-md mb-5">
                {code}
              </div>
              <button
                onClick={close}
                className="text-sm text-[#C05A3C] hover:underline"
                data-testid="newsletter-keep-shopping"
              >
                Keep shopping →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
